import React from "react";
import {autobind} from "core-decorators";
import {computed} from "mobx";
import {inject, observer} from "mobx-react";
import {Link} from "react-router-dom";
import {Button, InputNumber, message, notification, Space} from "antd";
import _debounce from "lodash/debounce";

import "./Members.scss";
import {getBillingPeriod, Plans} from "@/shared/plans";
import {getPrice, priceToString} from "../prices";
import {paths} from "@/routes";
import {getPriceEstimate, updateSubscription} from "@/lib/backend";
import loadChargebee, {getChargebeeInstance} from "@/lib/chargebee/loadChargebee";
import Store from "@/stores";

export interface ISubscriptionManagementProps {
  store?: Store;
}

interface ISubscriptionManagementState {
  modifySubscriptionVisible: boolean;
  modifySubscriptionLoading: boolean;
  singerAccountsChange: number;
  proratedPriceForExtraSingers: number | null;
}

@loadChargebee
@inject("store")
@autobind
@observer
export default class SubscriptionManagement extends React.Component<
  ISubscriptionManagementProps,
  ISubscriptionManagementState
> {
  constructor(props: ISubscriptionManagementProps) {
    super(props);
    this.state = {
      modifySubscriptionVisible: false,
      modifySubscriptionLoading: false,
      singerAccountsChange: 0,
      proratedPriceForExtraSingers: null,
    };
  }

  @computed({keepAlive: true})
  get chargebee() {
    const {stageStore} = this.props.store!;
    return getChargebeeInstance(stageStore.stage.chargebeeSite);
  }

  handleUpdatedSingerAccountsChanged(value: number | null) {
    const existingExtraSingers = this.props.store!.session.ensembleExtraSingers;
    if (existingExtraSingers === null || value === null) return;
    const newExtraSingers = value - Plans.Ensemble.includedMembers;
    const singerAccountsChange = newExtraSingers - existingExtraSingers;
    this.setState({
      singerAccountsChange,
      proratedPriceForExtraSingers: null,
    });
    if (singerAccountsChange > 0) {
      this.loadPriceDebounced(newExtraSingers);
    }
  }

  loadPriceDebounced = _debounce(this.loadPrice, 500);

  async loadPrice(extraSingers: number) {
    const activeSubscription = this.props.store!.session.activeSubscription!;
    try {
      const {proratedPriceForExtraSingers} = await getPriceEstimate({
        newExtraSingersValue: extraSingers,
        subscriptionId: activeSubscription.id,
      });
      if (proratedPriceForExtraSingers === undefined) {
        throw new Error("API error");
      }
      // Make sure the selected value is still the same, because the user might have changed it in
      // the meanwhile.
      const currentExtraSingers = this.props.store!.session.ensembleExtraSingers!;
      const newExtraSingers = currentExtraSingers + this.state.singerAccountsChange;
      if (newExtraSingers === extraSingers) {
        this.setState({proratedPriceForExtraSingers});
      }
    } catch (error) {
      const errorMessage = error?.toJSON?.()?.error ?? "Unknown error";
      message.error(`Price calculation failed: ${errorMessage}. Please refresh the page and try again.`, 5);
    }
  }

  async handleModifySubscriptionClick() {
    const activeSubscription = this.props.store!.session.activeSubscription;
    const currentExtraSingers = this.props.store!.session.ensembleExtraSingers;
    if (activeSubscription === undefined) {
      throw new Error("activeSubscription is undefined");
    }
    if (currentExtraSingers === null) {
      throw new Error("currentExtraSingers is null");
    }
    if (this.state.singerAccountsChange === 0) {
      throw new Error("No change in number of singers");
    }
    const newExtraSingers = currentExtraSingers + this.state.singerAccountsChange;
    const action = this.state.singerAccountsChange > 0 ? "add" : "remove";
    this.setState({modifySubscriptionLoading: true});
    try {
      await updateSubscription({
        subscriptionId: activeSubscription.id,
        newExtraSingersValue: newExtraSingers,
        action,
      });
    } catch (error) {
      const errorMessage = error?.toJSON?.()?.error ?? "Unknown error";
      notification.error({message: "Error", description: errorMessage, duration: 0, placement: "bottomRight"});
      return;
    } finally {
      this.setState({modifySubscriptionLoading: false});
    }
    if (action === "add") {
      await this.props.store!.session.fetchSessionData();
      message.success("Thanks! Your subscription has been updated.", 7);
    } else {
      message.success("Thanks! Your subscription will be updated at the beginning of the next billing term.", 7);
    }
    this.setState({modifySubscriptionVisible: false, singerAccountsChange: 0});
    // Refresh the session data to get the updated subscription members
    this.props.store!.session.fetchSessionData();
  }

  renderModifySubscription(extraSingers: number) {
    if (!this.state.modifySubscriptionVisible) {
      return <Button onClick={() => this.setState({modifySubscriptionVisible: true})}>Change number of members</Button>;
    }

    const newExtraSingers = extraSingers + this.state.singerAccountsChange;
    const extraSingersPrice = {
      Monthly: newExtraSingers * Plans.Ensemble.extraSinger.Monthly,
      Yearly: newExtraSingers * Plans.Ensemble.extraSinger.Yearly,
    };
    const totalEnsemblePrice = {
      Monthly: Plans.Ensemble.pricing.Monthly + extraSingersPrice.Monthly,
      Yearly: Plans.Ensemble.pricing.Yearly + extraSingersPrice.Yearly,
    };
    const session = this.props.store!.session;
    const billingPeriod = getBillingPeriod(session.activeSubscription!.plan_id);
    if (billingPeriod === undefined) throw new Error("Billing period is undefined");
    const newAllowedSingers = Plans.Ensemble.includedMembers + newExtraSingers;
    return (
      <>
        <h4>Change number of members</h4>
        <div className="singer-accounts">
          <span className="singer-accounts-label">Adjust the total number of members:</span>
          <InputNumber
            min={Plans.Ensemble.includedMembers}
            value={newAllowedSingers}
            onChange={this.handleUpdatedSingerAccountsChanged}
          />
        </div>
        <div className="extra-singer-pricing">
          <div>Subscription base price: {getPrice(Plans.Ensemble.pricing, billingPeriod, false, false)}</div>
          <div>
            Price for {newExtraSingers} extra member{newExtraSingers === 1 ? "" : "s"}:{" "}
            {getPrice(extraSingersPrice, billingPeriod, false, false)}
          </div>
          <div>
            Total subscription price: <strong>{getPrice(totalEnsemblePrice, billingPeriod, false, false)}</strong>
          </div>
        </div>
        {this.state.singerAccountsChange > 0 && (
          <p className="explanation">
            You are adding {this.state.singerAccountsChange} more member
            {this.state.singerAccountsChange === 1 ? "" : "s"} to your subscription.
            <br />
            Prorated price for the current billing term (charged immediately):{" "}
            {this.state.proratedPriceForExtraSingers === null ? (
              "Loading..."
            ) : (
              <strong>{priceToString(this.state.proratedPriceForExtraSingers)} €</strong>
            )}
          </p>
        )}
        {this.state.singerAccountsChange < 0 && (
          <p className="explanation">
            You are removing {-this.state.singerAccountsChange} member
            {this.state.singerAccountsChange === -1 ? "" : "s"} from your subscription.
            <br />
            This change will take effect when the next billing term starts.
            <br />
            You will not be refunded for the current billing term.
          </p>
        )}
        <Space className="singer-accounts-buttons">
          <Button
            type="primary"
            onClick={this.handleModifySubscriptionClick}
            disabled={
              this.state.singerAccountsChange === 0 ||
              (this.state.singerAccountsChange > 0 && this.state.proratedPriceForExtraSingers === null)
            }
            loading={this.state.modifySubscriptionLoading}
          >
            {this.state.singerAccountsChange > 0 ? "Pay & " : ""}Modify Subscription
          </Button>
          <Button onClick={() => this.setState({modifySubscriptionVisible: false, singerAccountsChange: 0})}>
            Cancel
          </Button>
        </Space>
      </>
    );
  }

  render() {
    const {session} = this.props.store!;
    const extraSingers = session.ensembleExtraSingers;
    // Do not show unless we have the Ensemble plan and singers are defined
    if (!session.hasPlanEnsemble) return null;
    if (extraSingers === null) return null;

    const allowedSingers = Plans.Ensemble.includedMembers + extraSingers;
    return (
      <>
        <h3 className="additional-singers-heading">Additional Ensemble Members</h3>
        <p>
          Your subscription currently allows for up to <strong>{allowedSingers} member accounts</strong> in your
          ensemble.
          <br />
          The first {Plans.Ensemble.includedMembers} member accounts are included in the base price and you have added{" "}
          {extraSingers} additional member{extraSingers === 1 ? "" : "s"}.
        </p>
        {this.renderModifySubscription(extraSingers)}
        <p className="invite-members">
          To invite new ensemble members or manage existing members, go to the{" "}
          <Link to={paths.selectedEnsemble()}>Ensemble</Link> page.
        </p>
      </>
    );
  }
}
